'use client'

import React from 'react'
import { useProductLaunchState } from '../utils/productLaunch'

/**
 * Compact badge + countdown for product cards.
 */
export function LaunchBadge({ product, className = '' }) {
  const launch = useProductLaunchState(product)
  if (!launch.badge) return null

  const bg = launch.locked
    ? (launch.bannerColor || '#ffcc32')
    : '#22c55e'
  const text = launch.locked ? '#000' : '#fff'

  return (
    <div
      className={`text-[10px] md:text-xs font-black uppercase px-2.5 py-1 rounded ${className}`}
      style={{ backgroundColor: bg, color: text }}
    >
      {launch.badge}
    </div>
  )
}

export function LaunchCountdown({ product, compact = false, className = '' }) {
  const launch = useProductLaunchState(product)
  if (!launch.locked) return null

  const bg = launch.bannerColor || '#ffcc32'

  if (compact) {
    return (
      <div
        className={`text-xs font-bold uppercase px-3 py-1.5 rounded-full text-black ${className}`}
        style={{ 
          backgroundColor: bg,
          boxShadow: `0 0 15px ${bg}50`
        }}
      >
        {launch.label}: {launch.formatted}
      </div>
    )
  }

  const { days, hours, minutes, seconds } = launch.parts
  const cells = [
    ...(days > 0 ? [{ value: days, label: 'Days' }] : []),
    { value: hours, label: 'Hours' },
    { value: minutes, label: 'Minutes' },
    { value: seconds, label: 'Seconds' },
  ]

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={{
        background: `linear-gradient(135deg, ${bg}10 0%, transparent 50%, ${bg}05 100%)`,
        borderRadius: '1.5rem',
        padding: '1.5rem',
        border: `2px solid ${bg}30`,
        boxShadow: `0 0 40px ${bg}20, inset 0 0 30px ${bg}10`
      }}
    >
      {/* Animated background elements */}
      <div 
        className="absolute top-0 left-0 w-full h-full opacity-30"
        style={{
          background: `radial-gradient(circle at 20% 50%, ${bg}20 0%, transparent 50%),
                      radial-gradient(circle at 80% 50%, ${bg}15 0%, transparent 50%)`,
          animation: 'pulse-glow 3s ease-in-out infinite'
        }}
      />
      
      {/* Header */}
      <div className="relative z-10 text-center mb-4">
        <p 
          className="text-sm font-black uppercase tracking-[0.3em] mb-1"
          style={{ 
            color: bg,
            textShadow: `0 0 20px ${bg}50`
          }}
        >
          {launch.label}
        </p>
        <div className="h-0.5 w-24 mx-auto" style={{ background: `linear-gradient(90deg, transparent, ${bg}, transparent)` }} />
      </div>

      {/* Timer cells with unique design */}
      <div className="relative z-10 flex items-center justify-center gap-4">
        {cells.map((cell, index) => (
          <div
            key={cell.label}
            className="relative group"
            style={{
              perspective: '1000px'
            }}
          >
            {/* Glowing border effect */}
            <div 
              className="absolute -inset-0.5 rounded-2xl opacity-75 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: `linear-gradient(135deg, ${bg}40 0%, ${bg}20 50%, ${bg}40 100%)`,
                filter: 'blur(8px)',
                animation: `border-glow 2s ease-in-out infinite ${index * 0.2}s`
              }}
            />
            
            {/* Main card */}
            <div 
              className="relative rounded-2xl bg-black/90 backdrop-blur-xl text-center px-4 py-4 border border-white/10"
              style={{
                transform: 'rotateX(5deg)',
                transformStyle: 'preserve-3d'
              }}
            >
              {/* Number display */}
              <div 
                className="text-3xl md:text-4xl font-black tabular-nums leading-none"
                style={{
                  background: `linear-gradient(180deg, #ffffff 0%, ${bg} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  filter: 'drop-shadow(0 0 10px rgba(255, 204, 50, 0.5))'
                }}
              >
                {String(cell.value).padStart(2, '0')}
              </div>
              
              {/* Label with decorative element */}
              <div className="mt-2 relative">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-400">
                  {cell.label}
                </div>
                <div 
                  className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-0.5"
                  style={{ 
                    background: bg,
                    boxShadow: `0 0 10px ${bg}`
                  }}
                />
              </div>
            </div>

            {/* Corner accents */}
            <div className="absolute top-1 left-1 w-2 h-2 rounded-tl-sm" style={{ background: bg, opacity: 0.8 }} />
            <div className="absolute top-1 right-1 w-2 h-2 rounded-tr-sm" style={{ background: bg, opacity: 0.8 }} />
            <div className="absolute bottom-1 left-1 w-2 h-2 rounded-bl-sm" style={{ background: bg, opacity: 0.8 }} />
            <div className="absolute bottom-1 right-1 w-2 h-2 rounded-br-sm" style={{ background: bg, opacity: 0.8 }} />
          </div>
        ))}
      </div>

      {/* Decorative bottom line */}
      <div className="relative z-10 mt-4 flex justify-center gap-1">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="w-1 h-1 rounded-full animate-pulse"
            style={{
              background: bg,
              opacity: 0.3 + (i * 0.1),
              animationDelay: `${i * 0.15}s`
            }}
          />
        ))}
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.5; }
        }
        @keyframes border-glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  )
}

/** Hook re-export for pages that need locked / badge state with cart buttons */
export { useProductLaunchState }
